import { useState } from "react";
import { Archive, Loader2, AlertOctagon, CheckCircle2 } from "lucide-react";
import { platformPost } from "./api/platformClient";

const makeIdempotencyKey = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  return `cc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const ArchiveTenantModal = ({ companyId, companyName, onClose, onArchived }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [state, setState] = useState("ready"); // ready | submitting | success | failure
  const [error, setError] = useState(null);
  const [idempotencyKey] = useState(() => makeIdempotencyKey());

  const submit = async () => {
    setState("submitting");
    setError(null);
    try {
      await platformPost(
        `/api/platform/companies/${companyId}/archive`,
        { idempotency_key: idempotencyKey, confirm: true }
      );
      setState("success");
    } catch (e) {
      setError(e);
      setState("failure");
    }
  };

  // Truthful failure copy. A 404 from the lifecycle endpoint specifically
  // means ShelfLife has not registered the route on this host yet; that is
  // different from a generic write failure and the operator needs to know.
  const failureMessage =
    error && error.status === 404
      ? "Lifecycle endpoint not available on this ShelfLife host yet. ShelfLife Phase 3D lifecycle writes (archive / unarchive / delete-test-tenant) are not registered on app.blueharborlabs.app. Confirm with the ShelfLife team that Phase 3D is promoted to production."
      : (error && error.message) || "Archive failed.";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm" data-testid="archive-modal">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
          <Archive className="h-5 w-5 text-amber-600" />
          <div className="flex-1">
            <h3 className="text-base font-semibold text-slate-900">Archive {companyName}?</h3>
            <p className="text-xs text-slate-500 mt-0.5">Reversible. Users lose access until unarchived.</p>
          </div>
        </div>

        {state === "success" ? (
          <div className="px-6 py-6 text-center" data-testid="archive-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-900">{companyName} archived.</p>
            <button
              type="button"
              data-testid="archive-success-close"
              onClick={() => { onArchived && onArchived(); onClose && onClose(); }}
              className="mt-4 px-4 py-2 rounded-lg bg-[#0d3b66] text-white text-sm font-medium hover:bg-[#0a2c4d]"
            >Done</button>
          </div>
        ) : (
          <>
            <div className="px-6 py-5 space-y-3 text-sm text-slate-700">
              <ul className="space-y-2 list-disc pl-5">
                <li>Users are signed out and cannot sign in until unarchived.</li>
                <li>Inventory, action history, and invoices are retained.</li>
                <li>Operation is reversible from the same panel after archive.</li>
              </ul>
              <label className="flex items-start gap-2 pt-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="mt-0.5"
                  data-testid="archive-acknowledge"
                />
                <span>I understand users will lose access.</span>
              </label>
              {state === "failure" && error && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-900 text-xs flex items-start gap-2" data-testid="archive-error">
                  <AlertOctagon className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{failureMessage}</span>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
              <button
                type="button"
                data-testid="archive-cancel"
                onClick={onClose}
                disabled={state === "submitting"}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >Cancel</button>
              <button
                type="button"
                data-testid="archive-confirm"
                onClick={submit}
                disabled={!acknowledged || state === "submitting"}
                className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {state === "submitting" && <Loader2 className="h-4 w-4 animate-spin" />}
                Archive
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
